import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Plus, MessageSquare, Hash } from 'lucide-react';
import { useChat } from '../../contexts/useChat';
import { authHeaders } from '../../utils/authHeaders';

function getInitials(name = '') {
  return name.split(/\s+/).map(w => w[0]).join('').toUpperCase().slice(0, 2) || '?';
}

function Avatar({ user, size = 7 }) {
  const cls = size === 7 ? 'w-7 h-7 text-[11px]' : 'w-8 h-8 text-xs';
  if (user?.avatarUrl) {
    return <img src={user.avatarUrl} alt={user.name} className={`${cls} rounded-full object-cover flex-shrink-0`} />;
  }
  return (
    <div className={`${cls} rounded-full bg-gray-700 flex items-center justify-center flex-shrink-0 font-semibold text-gray-300`}>
      {getInitials(user?.name)}
    </div>
  );
}

function AdminPicker({ allAdmins, selected, onChange, excludeIds = [] }) {
  const available = allAdmins.filter(a => !excludeIds.includes(a.id));
  return (
    <div className="border border-gray-700 rounded-lg overflow-hidden max-h-40 overflow-y-auto">
      {available.length === 0 && <p className="text-xs text-gray-500 px-3 py-2">추가할 직원이 없습니다</p>}
      {available.map(a => {
        const checked = selected.includes(a.id);
        return (
          <label key={a.id} className={`flex items-center gap-2 px-3 py-2 cursor-pointer transition-colors ${checked ? 'bg-brand/10' : 'hover:bg-gray-800'}`}>
            <input type="checkbox" className="accent-brand" checked={checked} onChange={e => {
              onChange(e.target.checked ? [...selected, a.id] : selected.filter(id => id !== a.id));
            }} />
            <span className="text-sm text-white">{a.name}</span>
            <span className="text-xs text-gray-500">{a.team || a.department || ''}</span>
          </label>
        );
      })}
    </div>
  );
}

function NewChannelModal({ onClose, onCreate, me }) {
  const [form, setForm] = useState({ name: '', description: '' });
  const [memberIds, setMemberIds] = useState([]);
  const [allAdmins, setAllAdmins] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetch('/api/admins/dropdown', { headers: authHeaders() })
      .then(r => r.json()).then(data => setAllAdmins(Array.isArray(data) ? data : [])).catch(() => {});
  }, []);

  const handleSubmit = async e => {
    e.preventDefault();
    if (!form.name.trim()) return;
    setSaving(true);
    await onCreate({ ...form, type: 'private', memberIds });
    setSaving(false);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/60" onClick={onClose}>
      <div className="bg-gray-900 border border-gray-700 rounded-xl w-80 p-5" onClick={e => e.stopPropagation()}>
        <h3 className="text-sm font-semibold text-white mb-4">새 채널 만들기</h3>
        <form onSubmit={handleSubmit} className="flex flex-col gap-3">
          <div>
            <label className="text-xs text-gray-400 mb-1 block">채널명 *</label>
            <input className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-brand" placeholder="채널명 입력" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} required />
          </div>
          <div>
            <label className="text-xs text-gray-400 mb-1 block">부제</label>
            <input className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-brand" placeholder="채널 설명 (선택)" value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} />
          </div>
          <div>
            <label className="text-xs text-gray-400 mb-1 block">멤버 선택</label>
            <AdminPicker allAdmins={allAdmins} selected={memberIds} onChange={setMemberIds} excludeIds={[me?.id]} />
            <p className="text-[10px] text-gray-500 mt-1">나 포함 {memberIds.length + 1}명</p>
          </div>
          <div className="flex gap-2 justify-end pt-1">
            <button type="button" onClick={onClose} className="px-3 py-1.5 text-xs text-gray-400 border border-gray-700 rounded-lg hover:bg-gray-800">취소</button>
            <button type="submit" disabled={saving} className="px-3 py-1.5 text-xs text-white bg-brand rounded-lg hover:bg-brand/80 disabled:opacity-50">{saving ? '생성 중...' : '만들기'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function EditChannelModal({ channel, onClose, onSave }) {
  const [form, setForm] = useState({ name: channel.name, description: channel.description || '' });
  const [saving, setSaving] = useState(false);
  const handleSubmit = async e => {
    e.preventDefault();
    if (!form.name.trim()) return;
    setSaving(true);
    await onSave(channel.id, { name: form.name.trim(), description: form.description });
    setSaving(false);
    onClose();
  };
  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/60" onClick={onClose}>
      <div className="bg-gray-900 border border-gray-700 rounded-xl w-80 p-5" onClick={e => e.stopPropagation()}>
        <h3 className="text-sm font-semibold text-white mb-4">채널 정보 수정</h3>
        <form onSubmit={handleSubmit} className="flex flex-col gap-3">
          <div>
            <label className="text-xs text-gray-400 mb-1 block">채널명 *</label>
            <input
              className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-brand"
              value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} autoFocus required
            />
          </div>
          <div>
            <label className="text-xs text-gray-400 mb-1 block">부제</label>
            <input
              className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-brand"
              placeholder="채널 설명 (선택)" value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))}
            />
          </div>
          <div className="flex gap-2 justify-end pt-1">
            <button type="button" onClick={onClose} className="px-3 py-1.5 text-xs text-gray-400 border border-gray-700 rounded-lg hover:bg-gray-800">취소</button>
            <button type="submit" disabled={saving} className="px-3 py-1.5 text-xs text-white bg-brand rounded-lg hover:bg-brand/80 disabled:opacity-50">{saving ? '저장 중...' : '저장'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ChannelItem({ ch, isActive, onSelect, canManage, onEdit, onDelete }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuPos, setMenuPos] = useState({ top: 0, right: 0 });
  const menuRef = useRef(null);
  const btnRef = useRef(null);
  useEffect(() => {
    if (!menuOpen) return;
    const handler = e => {
      if (menuRef.current?.contains(e.target)) return;
      if (btnRef.current?.contains(e.target)) return;
      setMenuOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [menuOpen]);

  const toggleMenu = e => {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    setMenuPos({ top: rect.bottom + 4, right: window.innerWidth - rect.right });
    setMenuOpen(v => !v);
  };

  return (
    <div className="relative group transition-all">
      <button
        onClick={() => onSelect(ch)}
        className={`w-full flex items-center gap-2 px-2 py-1.5 text-left text-xs transition-colors rounded-md mx-1 ${
          isActive ? 'bg-brand/20 text-brand font-medium' : 'text-gray-400 hover:bg-gray-700/50 hover:text-white'
        }`}
        style={{ width: 'calc(100% - 8px)' }}
      >
        <Hash size={12} className="flex-shrink-0 opacity-60" />
        <span className="truncate flex-1">{ch.name}</span>
        {ch.unreadCount > 0 && (
          <span className="flex-shrink-0 bg-red-500 text-white text-[9px] font-bold rounded-full min-w-[1rem] h-4 flex items-center justify-center px-1">
            {ch.unreadCount > 99 ? '99+' : ch.unreadCount}
          </span>
        )}
      </button>
      {canManage && (
        <div className="absolute right-2 top-1/2 -translate-y-1/2">
          <button
            ref={btnRef}
            onClick={toggleMenu}
            className="opacity-0 group-hover:opacity-100 w-5 h-5 flex items-center justify-center rounded hover:bg-gray-600 text-gray-400 text-xs font-bold"
          >···</button>
          {menuOpen && createPortal(
            <div
              ref={menuRef}
              className="fixed z-[9999] bg-gray-800 border border-gray-700 rounded-lg py-1 min-w-[9rem]"
              style={{ top: menuPos.top, right: menuPos.right }}
            >
              <button onClick={e => { e.stopPropagation(); setMenuOpen(false); onEdit(ch); }} className="w-full text-left px-3 py-1.5 text-xs text-gray-300 hover:bg-gray-700">채널 정보 수정</button>
              <button onClick={e => { e.stopPropagation(); setMenuOpen(false); onDelete(ch); }} className="w-full text-left px-3 py-1.5 text-xs text-red-400 hover:bg-gray-700">채널 삭제</button>
            </div>,
            document.body
          )}
        </div>
      )}
    </div>
  );
}

function PersonItem({ admin, dmChannel, isActive, onOpen }) {
  const lastMsg = dmChannel?.lastMessage;

  const preview = (() => {
    if (!lastMsg?.content) return null;
    try {
      const parsed = JSON.parse(lastMsg.content);
      if (parsed.url) return parsed.name || '파일';
    } catch {}
    return lastMsg.content.slice(0, 28);
  })();

  return (
    <button
      onClick={() => onOpen(admin)}
      className={`w-full flex items-center gap-2.5 px-2 py-1.5 text-left transition-colors rounded-md mx-1 ${
        isActive ? 'text-brand' : 'text-gray-300 hover:bg-gray-700/50 hover:text-white'
      }`}
      style={{ width: 'calc(100% - 8px)' }}
    >
      <Avatar user={admin} size={7} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-1">
          <span className={`text-xs font-medium truncate ${isActive ? 'text-brand' : ''}`}>
            {admin.name}{admin.rank ? ` ${admin.rank}` : ''}
          </span>
          {dmChannel?.unreadCount > 0 && (
            <span className="flex-shrink-0 bg-red-500 text-white text-[9px] font-bold rounded-full min-w-[1rem] h-4 flex items-center justify-center px-1">
              {dmChannel.unreadCount > 99 ? '99+' : dmChannel.unreadCount}
            </span>
          )}
        </div>
        <p className="text-[10px] text-gray-500 truncate leading-tight">
          {[admin.department, admin.team].filter(Boolean).join(' / ')}
          {preview && <span className="ml-1 text-gray-600">· {preview}</span>}
        </p>
      </div>
    </button>
  );
}

export default function ChatSidebar() {
  const {
    me, channels, activeChannel,
    showNewModal, setShowNewModal,
    selectChannel, createChannel, renameChannel, deleteChannel, openDm,
    requestChannelId, setRequestChannelId,
  } = useChat();

  const [editTarget, setEditTarget] = useState(null);
  const [allAdmins, setAllAdmins] = useState([]);

  useEffect(() => {
    fetch('/api/admins/dropdown', { headers: authHeaders() })
      .then(r => r.json())
      .then(data => setAllAdmins(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!requestChannelId || !channels.length) return;
    const ch = channels.find(c => Number(c.id) === Number(requestChannelId));
    if (ch) { selectChannel(ch); setRequestChannelId(null); }
  }, [requestChannelId, channels, selectChannel, setRequestChannelId]);

  const isSuperadmin = me?.role === 'superadmin';
  const myId = me?.id;

  const regularCh = channels.filter(c => c.type !== 'dm');

  // 직원 목록 (나 제외)
  const otherAdmins = allAdmins.filter(a => a.id !== myId);

  // 각 직원의 DM 채널 매핑
  const getDmChannel = (adminId) => {
    const [a, b] = [myId, adminId].sort((x, y) => x - y);
    const dmName = `dm_${a}_${b}`;
    return channels.find(ch => ch.type === 'dm' && ch.name === dmName) || null;
  };

  // 최근 메시지 또는 unread 있는 사람을 위로 정렬
  const sortedAdmins = [...otherAdmins].sort((x, y) => {
    const cx = getDmChannel(x.id);
    const cy = getDmChannel(y.id);
    const ux = cx?.unreadCount || 0;
    const uy = cy?.unreadCount || 0;
    if (ux !== uy) return uy - ux;
    const tx = cx?.lastMessage?.createdAt ? new Date(cx.lastMessage.createdAt).getTime() : 0;
    const ty = cy?.lastMessage?.createdAt ? new Date(cy.lastMessage.createdAt).getTime() : 0;
    return ty - tx;
  });

  return (
    <>
      {showNewModal && <NewChannelModal onClose={() => setShowNewModal(false)} onCreate={createChannel} me={me} />}
      {editTarget && <EditChannelModal channel={editTarget} onClose={() => setEditTarget(null)} onSave={renameChannel} />}

      <div className="flex flex-col h-full overflow-hidden">
        {/* 팀 채널 섹션 */}
        <div className="flex items-center justify-between px-3 pt-3 pb-1 flex-shrink-0">
          <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider">채널</span>
          <button
            onClick={() => setShowNewModal(true)}
            className="w-5 h-5 flex items-center justify-center rounded hover:bg-gray-700 text-gray-500 hover:text-white"
            title="새 채널 만들기"
          >
            <Plus size={13} />
          </button>
        </div>

        <div className="flex-shrink-0">
          {regularCh.length === 0 ? (
            <button
              onClick={() => setShowNewModal(true)}
              className="w-full text-left px-3 py-1.5 text-xs text-gray-600 hover:text-gray-400 transition-colors"
            >
              + 채널 만들기
            </button>
          ) : (
            regularCh.map((ch) => {
              const canManageCh = isSuperadmin || ch.createdBy === myId;
              return (
                <ChannelItem
                  key={ch.id} ch={ch}
                  isActive={activeChannel?.id === ch.id}
                  onSelect={selectChannel}
                  canManage={canManageCh}
                  onEdit={setEditTarget}
                  onDelete={deleteChannel}
                />
              );
            })
          )}
        </div>

        {/* 구분선 */}
        <div className="mx-3 my-2 border-t border-gray-800 flex-shrink-0" />

        {/* 다이렉트 메시지 섹션 */}
        <div className="flex items-center px-3 pb-1.5 flex-shrink-0">
          <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider flex items-center gap-1">
            <MessageSquare size={10} /> 다이렉트 메시지
          </span>
        </div>

        <div className="flex-1 overflow-y-auto pb-2">
          {sortedAdmins.length === 0 ? (
            <p className="px-3 py-2 text-xs text-gray-600">직원 정보를 불러오는 중...</p>
          ) : (
            sortedAdmins.map(admin => {
              const dmCh = getDmChannel(admin.id);
              const isActive = activeChannel?.id === dmCh?.id;
              return (
                <PersonItem
                  key={admin.id}
                  admin={admin}
                  dmChannel={dmCh}
                  isActive={isActive}
                  onOpen={openDm}
                />
              );
            })
          )}
        </div>
      </div>
    </>
  );
}
